import React from 'react';
import { useLayout } from 'shared/contexts/LayoutContext';
import { useStateContext } from 'shared/contexts/ContextProvider';

const formatDate = (date) => (date ? new Date(date).toLocaleDateString('ru-RU') : '—');
const formatNumber = (number) => {
  if (number === null || number === undefined) return '—';
  return number.toLocaleString('ru-RU', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
};
const formatPercent = (number) => {
  if (number === null || number === undefined) return '—';
  return `${(number * 100).toFixed(2)}%`;
};

const SnapItem = ({ item }) => {
  const { closeModal } = useLayout();
  const { currentColor } = useStateContext();

  const snapName = item.meta?.name || `${item.date} - ${item.store?.meta?.name || item.store_name || ''} - ${item.product?.meta?.name || item.product_name || ''}`;

  return (
    <div className='max-w-4xl mx-auto p-4'>
      {/* Header Section */}
      <div className='flex justify-between items-start mb-6'>
        <div>
          <h1 className='text-xl font-semibold mb-1'>{snapName}</h1>
          <p className='text-gray-500 text-xs'>
            Создан {formatDate(item.created)} | Обновлен {formatDate(item.updated)}
          </p>
        </div>
      </div>

      {/* Basic Information Section */}
      <div className='bg-white rounded-lg shadow-sm border mb-6'>
        <div className='p-4 border-b'>
          <h2 className='font-medium text-sm'>ОСНОВНАЯ ИНФОРМАЦИЯ</h2>
        </div>
        <div className='p-4'>
          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Дата</span>
                <span className='text-sm'>{formatDate(item.date)}</span>
              </div>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Магазин</span>
                <span className='text-sm text-blue-600'>{item.store?.meta?.name || item.store_name || '—'}</span>
              </div>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Товар</span>
                <span className='text-sm text-blue-600'>{item.product?.meta?.name || item.product_name || '—'}</span>
              </div>
            </div>
            <div className='space-y-2'>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Цена</span>
                <span className='text-sm'>{formatNumber(item.price)}</span>
              </div>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Себестоимость</span>
                <span className='text-sm'>{formatNumber(item.cost)}</span>
              </div>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Маржа</span>
                <span className='text-sm'>{formatPercent(item.margin)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Inventory Metrics Section */}
      <div className='bg-white rounded-lg shadow-sm border mb-6'>
        <div className='p-4 border-b'>
          <h2 className='font-medium text-sm'>СКЛАДСКИЕ ОСТАТКИ</h2>
        </div>
        <div className='p-4'>
          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Остаток на начало</span>
                <span className='text-sm'>{formatNumber(item.qty_open)}</span>
              </div>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Сумма на начало</span>
                <span className='text-sm'>{formatNumber(item.sum_open)}</span>
              </div>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Себестоимость на начало</span>
                <span className='text-sm'>{formatNumber(item.purchase_open)}</span>
              </div>
            </div>
            <div className='space-y-2'>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Остаток на конец</span>
                <span className='text-sm'>{formatNumber(item.qty_close)}</span>
              </div>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Сумма на конец</span>
                <span className='text-sm'>{formatNumber(item.sum_close)}</span>
              </div>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Себестоимость на конец</span>
                <span className='text-sm'>{formatNumber(item.purchase_close)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Movement Metrics Section */}
      <div className='bg-white rounded-lg shadow-sm border mb-6'>
        <div className='p-4 border-b'>
          <h2 className='font-medium text-sm'>ДВИЖЕНИЕ ТОВАРА</h2>
        </div>
        <div className='p-4'>
          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Приход</span>
                <span className='text-sm'>{formatNumber(item.qty_in)}</span>
              </div>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Расход</span>
                <span className='text-sm'>{formatNumber(item.qty_out)}</span>
              </div>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Закупки</span>
                <span className='text-sm'>{formatNumber(item.purchases_qty)}</span>
              </div>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Возвраты продаж</span>
                <span className='text-sm'>{formatNumber(item.return_sales_qty)}</span>
              </div>
            </div>
            <div className='space-y-2'>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Возвраты закупок</span>
                <span className='text-sm'>{formatNumber(item.return_purchases_qty)}</span>
              </div>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Перемещения</span>
                <span className='text-sm'>{formatNumber(item.movements_qty)}</span>
              </div>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Изменения (исходящие)</span>
                <span className='text-sm'>{formatNumber(item.changes_out_qty)}</span>
              </div>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Изменения (входящие)</span>
                <span className='text-sm'>{formatNumber(item.changes_in_qty)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Metrics Section */}
      <div className='bg-white rounded-lg shadow-sm border mb-6'>
        <div className='p-4 border-b'>
          <h2 className='font-medium text-sm'>ПРОДАЖИ</h2>
        </div>
        <div className='p-4'>
          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Количество продаж</span>
                <span className='text-sm'>{item.sales_count || 0}</span>
              </div>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Количество проданных</span>
                <span className='text-sm'>{formatNumber(item.sales_qty)}</span>
              </div>
            </div>
            <div className='space-y-2'>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Сумма продаж</span>
                <span className='text-sm text-green-600'>{formatNumber(item.sales_sum)}</span>
              </div>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Себестоимость продаж</span>
                <span className='text-sm'>{formatNumber(item.sales_purchase)}</span>
              </div>
              <div className='flex justify-between border-b border-dotted pb-2'>
                <span className='text-gray-600 text-sm'>Прибыль от продаж</span>
                <span className='text-sm text-green-600 font-semibold'>{formatNumber(item.sales_profit)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Metrics Section */}
      {(item.turn !== null || item.net_change !== null || item.stock_value_change !== null || item.cost_value_change !== null) && (
        <div className='bg-white rounded-lg shadow-sm border'>
          <div className='p-4 border-b'>
            <h2 className='font-medium text-sm'>ДОПОЛНИТЕЛЬНЫЕ МЕТРИКИ</h2>
          </div>
          <div className='p-4'>
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                {item.turn !== null && (
                  <div className='flex justify-between border-b border-dotted pb-2'>
                    <span className='text-gray-600 text-sm'>Оборачиваемость</span>
                    <span className='text-sm'>{formatNumber(item.turn)}</span>
                  </div>
                )}
                {item.net_change !== null && (
                  <div className='flex justify-between border-b border-dotted pb-2'>
                    <span className='text-gray-600 text-sm'>Чистое изменение</span>
                    <span className='text-sm'>{formatNumber(item.net_change)}</span>
                  </div>
                )}
              </div>
              <div className='space-y-2'>
                {item.stock_value_change !== null && (
                  <div className='flex justify-between border-b border-dotted pb-2'>
                    <span className='text-gray-600 text-sm'>Изменение стоимости запасов</span>
                    <span className='text-sm'>{formatNumber(item.stock_value_change)}</span>
                  </div>
                )}
                {item.cost_value_change !== null && (
                  <div className='flex justify-between border-b border-dotted pb-2'>
                    <span className='text-gray-600 text-sm'>Изменение себестоимости</span>
                    <span className='text-sm'>{formatNumber(item.cost_value_change)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SnapItem;
